import {BaseGraphNode} from "#graph/nodes/base/base-graph-node"
import type {BaseGraphNodeDefinition, NodeParameters} from "#graph/nodes/base/types"

// These static properties are enforced via the `@registerGraphNode` decorator.
// Each registered node must define at least the `definition` and `nodeClass` properties to become serializable.
export type RegistrableNodeClass = {
    prototype: {
        definition: BaseGraphNodeDefinition
    }
}

export type RegisteredNodeClass = (new (options: {parameters: NodeParameters; disableValidation?: boolean}) => BaseGraphNode) & {
    definition: BaseGraphNodeDefinition
}

export const DEFAULT_NAMESPACE = "default"

const nodeFactories = new Map<string, Map<string, RegisteredNodeClass>>()

const getOrCreateFactory = (namespace: string) => {
    let factory = nodeFactories.get(namespace)
    if (!factory) {
        factory = new Map()
        nodeFactories.set(namespace, factory)
    }
    return factory
}

export function getRegisteredNode(name: string, namespace: string = DEFAULT_NAMESPACE) {
    const factory = nodeFactories.get(namespace)
    const retVal = factory?.get(name)
    if (!retVal) {
        throw new Error(`Node class ${name} not found in namespace ${namespace}, forgot to decorate it with @registerGraphNode?`)
    }
    return retVal
}

export function getAllRegisteredNodes(namespace: string = DEFAULT_NAMESPACE): RegisteredNodeClass[] {
    const factory = nodeFactories.get(namespace)
    if (!factory) return []
    return [...new Set(factory.values())]
}

const registerInto = (namespace: string, targetClass: RegistrableNodeClass) => {
    const definition = targetClass.prototype.definition
    const nodeClass = definition.nodeClass
    if (!("prototype" in targetClass) || !(targetClass.prototype instanceof BaseGraphNode)) {
        throw new Error(`Class ${nodeClass} must extend BaseGraphNode to be eligible for registration`)
    }
    const versionChain = definition.versionChain

    if (versionChain && !Array.isArray(versionChain)) {
        throw new Error(`Version chain not defined, forgot to decorate ${nodeClass} with @declareNode()?`)
    }

    const factory = getOrCreateFactory(namespace)
    const register = (name: string) => {
        if (name === "GraphRef") {
            throw new Error(`Entity name ${name} is reserved`)
        }
        if (factory.has(name)) {
            throw new Error(`Node class ${name} already registered in namespace ${namespace}, double-check your import paths`)
        }
        factory.set(name, targetClass as RegisteredNodeClass)
    }

    register(nodeClass)
    versionChain?.forEach((version) => {
        if (version.nodeClass !== undefined) {
            register(version.nodeClass)
        }
    })
}

export function registerGraphNode(targetClass: RegistrableNodeClass) {
    registerInto(DEFAULT_NAMESPACE, targetClass)
}

export function registerGraphNodeIn(namespace: string) {
    return (targetClass: RegistrableNodeClass) => registerInto(namespace, targetClass)
}
